import React, { ReactElement, FC } from 'react';
import { APPS_ROUTES, MENU_PERMISSIONS, FEATURE_FLAGS, POLICY_PERMISSIONS } from '@trip-ux/constants';
import { ReduxStore, UserProfileStore } from '@trip-ux/redux-store';
import { filter, first, get } from 'lodash';
import { useSelector } from 'react-redux';
import { NavMenu, NavMenuItem } from '@trip-ux/components';

type MenuWithRole = FC<{
	sideNavContent: unknown;
  children?: React.ReactNode;
 	render?: (props: { sideNavContent: unknown }) => ReactElement;
}>;

type TabWithRole = FC<{
 	tabList: { id: string; label: string }[];
  children?: React.ReactNode;
 	permissions?: { [id: string]: string | string[] };
 	render?: (props: { items }) => ReactElement;
}>;

type RouteWithRole = FC<{
 	roles: string | string[];
  children?: React.ReactNode;
 	additionalCheck?: (user: UserProfileStore) => boolean;
 	sideNavContent?: unknown;
}>;

type ComponentWithRole = FC<{
 	roles: string | string[];
  children?: React.ReactNode;
 	additionalCheck?: (user: UserProfileStore) => boolean;
 	render?: (props: { hasRole: boolean }) => ReactElement;
}>;

const getHasRole = (
  user: UserProfileStore,
  roles: string | string[],
  additionalCheck?: (user: UserProfileStore) => boolean
): boolean => {
  const rolesToCheck = typeof roles === 'string' ? [roles] : roles;

  let hasRole = rolesToCheck.every(role => get(user, 'claims', []).includes(role));
  if (additionalCheck) {
    hasRole = hasRole && additionalCheck(user);
  }
  return hasRole;
};

// Local implementation to avoid circular dependency with redux-store and common
// This is identical to checkFeatureFlag in @trip-ux/common/utils
const checkFeatureFlag = (featureFlags: { name: string; value: boolean }[], flagName: string): boolean => {
  return featureFlags.some(flag => flag.name === flagName && flag.value === true);
};

// Check if a navId is enabled based on feature flags
const isNavIdEnabled = (navId: string, userProfile: UserProfileStore): boolean => {
  const featureFlags = userProfile.featureFlags || [];

  // Map navIds to their corresponding feature flags
  if (navId === 'advisorAdmin') {
    return checkFeatureFlag(featureFlags, FEATURE_FLAGS.ADVISOR);
  }

  // If no specific feature flag is required, route is enabled
  return true;
};

const getAllowedMenuItems = (menuList: NavMenuItem[], user: UserProfileStore): NavMenuItem[] => {
  const isQuoteOnly = get(user, 'claims', []).includes(POLICY_PERMISSIONS.POLICY_QUOTE_ONLY);

  return filter(menuList, menuItem => {
    const navId = get(menuItem, 'navId');

    // Hide Policy Maintenance entry for quote-only users (e.g. Back Office, Marketing Assistant)
    if (navId === 'maintenance' && isQuoteOnly) {
      return false;
    }

    const requiredPermissions = get(MENU_PERMISSIONS, navId, []);
    const hasPermission = getHasRole(user, requiredPermissions);

    // Also check if the route is enabled by feature flags using navId
    const isEnabled = navId ? isNavIdEnabled(navId, user) : true;

    return hasPermission && isEnabled;
  });
};

// Hide the destinationMultiplierSub sub-item unless the DESTINATION_PRICING flag
// is on AND the user has the required claim for it.
const stripDestinationMultiplierSub = (
  menuList: NavMenuItem[],
  user: UserProfileStore
): NavMenuItem[] => {
  const featureFlags = user.featureFlags || [];
  const requiredPermissions = get(MENU_PERMISSIONS, 'destinationMultiplierSub', []);
  const isAllowed =
    checkFeatureFlag(featureFlags, FEATURE_FLAGS.DESTINATION_PRICING) && getHasRole(user, requiredPermissions);

  if (isAllowed) {
    return menuList;
  }

  return menuList.map(item => {
    const subMenuList = (item as { subMenuList?: NavMenuItem[] }).subMenuList;
    if (!subMenuList || subMenuList.length === 0) {
      return item;
    }
    return {
      ...item,
      subMenuList: subMenuList.filter(sub => sub.navId !== 'destinationMultiplierSub')
    } as NavMenuItem;
  });
};

const Menu: MenuWithRole = ({ sideNavContent, render }): ReactElement => {
  const user = useSelector((store: ReduxStore) => store.userProfile);
  const menuList = get(sideNavContent, 'menuList', []);

  const allowedMenuItems = stripDestinationMultiplierSub(getAllowedMenuItems(menuList, user), user);

  if (render) {
    return render({ sideNavContent: { ...(sideNavContent as NavMenu), menuList: allowedMenuItems } });
  }
  return null;
};

const Tab: TabWithRole = ({ tabList, permissions, render }): ReactElement => {
  const user = useSelector((store: ReduxStore) => store.userProfile);

  const allowedMenuItems = filter(tabList, menuItem => {
    const tabId = get(menuItem, 'id');
    const requiredPermissions = get(permissions, tabId, []);
    return getHasRole(user, requiredPermissions);
  });

  if (render) {
    return render({ items: allowedMenuItems });
  }
  return null;
};

export const Route: RouteWithRole = ({ roles, additionalCheck, children, sideNavContent }): ReactElement => {
  const { user, locale } = useSelector((store: ReduxStore) => ({
    user: store.userProfile,
    locale: store.config.locale
  }));

  const hasRole = getHasRole(user, roles, additionalCheck);
  if (!hasRole) {
    const menuList = get(sideNavContent, 'menuList', []);
    const allowedMenuItems = getAllowedMenuItems(menuList, user);
    const validLandingRoute = first(allowedMenuItems);
    // Use redirectTo helper
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { redirectTo } = require('../navigation');

    if (validLandingRoute?.href) {
      redirectTo(get(validLandingRoute, 'href'));
      return null;
    } else if (validLandingRoute?.subMenuList[0]?.href) {
      redirectTo(get(validLandingRoute, 'subMenuList[0].href'));
      return null;
    } else {
      redirectTo(`${APPS_ROUTES.en.FORBIDDEN_ERROR}?lang=${locale}`);
      return null;
    }
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export const Component: ComponentWithRole = ({ roles, additionalCheck, render, children }): ReactElement => {
  const user = useSelector((store: ReduxStore) => store.userProfile);

  const hasRole = getHasRole(user, roles, additionalCheck);

  if (render) {
    return render({ hasRole });
  } else if (hasRole) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }
  return null;
};

export const WithRole: {
  displayName?: string;
  Menu?: MenuWithRole;
  Tab?: TabWithRole;
  Route?: RouteWithRole;
  Component?: ComponentWithRole;
} = {
  displayName: 'WithRole'
};
WithRole.Menu = Menu;
WithRole.Tab = Tab;
WithRole.Route = Route;
WithRole.Component = Component;
export default WithRole;
